import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Minus, UserPlus, Upload, Users, SortAsc, Mail, ShieldCheck, X } from 'lucide-react';
import { api } from '../services/api';
import { Player } from '../types';
import { CreatePlayerModal } from '../components/CreatePlayerModal';
import { ImportPlayersModal } from '../components/ImportPlayersModal';

interface Team {
  id: string;
  name: string;
  ageGroup: string;
  gender?: string;
}

interface AssessmentSummary {
  playerId: string;
  count: number;
  avgScore: number;
  trend?: 'up' | 'down' | 'same';
}

type ViewMode = 'position' | 'alphabetical';

const POSITION_ORDER = ['Goalkeeper', 'Defender', 'Midfield', 'Winger', 'Forward'];

function pluralLabel(position: string): string {
  const map: Record<string, string> = {
    Goalkeeper: 'Goalkeepers',
    Defender: 'Defenders',
    Midfield: 'Midfielders',
    Winger: 'Wingers',
    Forward: 'Forwards',
  };
  return map[position] || position;
}

function calculateAge(dateOfBirth: string | Date): number {
  const today = new Date();
  const birth = new Date(dateOfBirth);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

export function NewPlayersPage() {
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('position');
  const [assessments, setAssessments] = useState<Map<string, AssessmentSummary>>(new Map());
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [showReportTip, setShowReportTip] = useState(false);

  // Show the report tip on first visit to players page
  useEffect(() => {
    if (!localStorage.getItem('gamepath_players_tip_seen')) {
      setShowReportTip(true);
    }
  }, []);

  const fetchPlayers = useCallback(async (teamId: string) => {
    try {
      const res = await api.get(`/teams/${teamId}/players`);
      const data: Player[] = res.data.data || [];
      setPlayers(data);

      // Fetch assessment summaries for each player
      const summaryMap = new Map<string, AssessmentSummary>();
      for (const p of data) {
        try {
          const aRes = await api.get(`/assessments/player/${p.id}`);
          const list = aRes.data.data || [];
          if (list.length > 0) {
            let total = 0;
            let count = 0;
            for (const a of list) {
              if (a.scores) {
                const corners = ['technical', 'physical', 'psychological', 'social'];
                for (const c of corners) {
                  const vals = Object.values(a.scores[c] || {}).filter((v): v is number => v !== undefined);
                  total += vals.reduce((s: number, v: number) => s + v, 0);
                  count += vals.length;
                }
              }
            }
            summaryMap.set(p.id, {
              playerId: p.id,
              count: list.length,
              avgScore: count > 0 ? parseFloat((total / count).toFixed(1)) : 0,
            });
          }
        } catch { /* no assessments */ }
      }
      setAssessments(summaryMap);
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const teamsRes = await api.get('/teams');
        const teamsData: Team[] = teamsRes.data.data || [];
        setTeams(teamsData);
        if (teamsData.length > 0) {
          setSelectedTeam(teamsData[0].id);
          await fetchPlayers(teamsData[0].id);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [fetchPlayers]);

  const handleTeamChange = async (teamId: string) => {
    setSelectedTeam(teamId);
    setLoading(true);
    await fetchPlayers(teamId);
    setLoading(false);
  };

  const handlePlayerCreated = (player: Player) => {
    setPlayers(prev => [...prev, player]);
    setShowAddPlayer(false);
  };

  const handlePlayersImported = (imported: Player[]) => {
    setPlayers(prev => [...prev, ...imported]);
    setShowImportCSV(false);
  };

  const currentTeam = teams.find(t => t.id === selectedTeam);
  const assessedCount = players.filter(p => assessments.has(p.id)).length;
  const pendingCount = players.length - assessedCount;

  // Group players by position
  const groupedByPosition = POSITION_ORDER.map(pos => ({
    position: pos,
    label: pluralLabel(pos),
    players: players.filter(p => p.position === pos),
  })).filter(g => g.players.length > 0);

  // Alphabetical sort
  const alphabeticalPlayers = [...players].sort((a, b) =>
    `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)
  );

  if (loading && teams.length === 0) {
    return (
      <div className="p-4 lg:p-8 max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>)}
          </div>
          <div className="h-64 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  const teamLabel = currentTeam
    ? `${currentTeam.name}${currentTeam.gender ? ` ${currentTeam.gender}` : ''} • ${players.length} player${players.length !== 1 ? 's' : ''}`
    : 'Select a team';

  // ─── Player Card ──────────────────────────────────────────────────────────
  const PlayerCard = ({ player }: { player: Player }) => {
    const age = calculateAge(player.dateOfBirth);
    const summary = assessments.get(player.id);

    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 hover:border-green-300 hover:shadow-sm transition-all">
        <div className="mb-1">
          <h3 className="font-semibold text-gray-900">{player.firstName} {player.lastName}</h3>
          <p className="text-sm text-gray-500">{player.position || 'Position not set'} • Age {age}</p>
        </div>

        <div className="my-3 py-1">
          {summary ? (
            <div className="flex items-center gap-3">
              <span className="text-xl font-bold text-gray-900">{summary.avgScore}</span>
              <span className="text-xs text-gray-500">Avg Score</span>
              <span className="text-xl font-bold text-gray-900 ml-2">{summary.count}</span>
              <span className="text-xs text-gray-500">Assessment{summary.count !== 1 ? 's' : ''}</span>
              {summary.trend === 'up' && <span className="text-green-500 text-sm">↑</span>}
              {summary.trend === 'down' && <span className="text-red-500 text-sm">↓</span>}
            </div>
          ) : (
            <p className="text-sm text-gray-400 flex items-center gap-2">
              <Minus className="w-4 h-4" />
              Not yet assessed
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <Link
            to={`/players/${player.id}`}
            className="flex-1 px-4 py-2.5 text-center text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors min-h-[44px] flex items-center justify-center"
          >
            View Profile
          </Link>
          <Link
            to={`/assess/${player.id}`}
            className="flex-1 px-4 py-2.5 text-center text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors min-h-[44px] flex items-center justify-center gap-1.5"
          >
            <Activity className="w-4 h-4" />
            Assess
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Automatic Assessment Reports tooltip — first visit */}
      {showReportTip && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/40" onClick={() => { setShowReportTip(false); localStorage.setItem('gamepath_players_tip_seen', 'true'); }} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Automatic Assessment Reports</h2>
                    <p className="text-sm text-gray-500">Keep players and parents informed</p>
                  </div>
                </div>
                <button onClick={() => { setShowReportTip(false); localStorage.setItem('gamepath_players_tip_seen', 'true'); }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                After each assessment, <span className="font-semibold">PDF reports are automatically emailed</span> to keep everyone in the loop about player progress and development.
              </p>

              {/* Players 18+ */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-3">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Players 18 years or older</p>
                    <p className="text-sm text-gray-600 mt-0.5">Add a <span className="font-semibold">contact email</span> for the player to receive their assessment reports directly.</p>
                  </div>
                </div>
              </div>

              {/* Players under 18 */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Players under 18 years</p>
                    <p className="text-sm text-gray-600 mt-0.5">Add <span className="font-semibold">parent/guardian details</span>:</p>
                    <ul className="text-sm text-gray-600 mt-1 space-y-0.5">
                      <li>• First name and surname</li>
                      <li>• Email address</li>
                      <li>• Optional: Add multiple parents/guardians</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Pro tip */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">💡 Pro Tip:</span> You can toggle off automatic report sending on the assessment page if you prefer to share reports manually.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button onClick={() => { setShowReportTip(false); localStorage.setItem('gamepath_players_tip_seen', 'true'); }}
                  className="flex-1 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]">
                  Got it, thanks!
                </button>
                <button onClick={() => { setShowReportTip(false); localStorage.setItem('gamepath_players_tip_seen', 'true'); }}
                  className="flex-1 px-4 py-2.5 text-gray-700 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors min-h-[44px]">
                  Add Contact Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-1">Players</h1>
          <p className="text-gray-500">{teamLabel}</p>
        </div>
        <button onClick={() => setShowReportTip(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors min-h-[44px]">
          <Mail className="w-4 h-4" />
          Report Info
        </button>
      </div>

      {/* Team Selector */}
      {teams.length > 1 && (
        <div className="mb-6">
          <select
            value={selectedTeam}
            onChange={(e) => handleTeamChange(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 min-h-[44px]"
          >
            {teams.map(t => (
              <option key={t.id} value={t.id}>{t.name} - {t.ageGroup}</option>
            ))}
          </select>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 lg:gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-1">Total Players</p>
          <p className="text-2xl font-bold text-gray-900">{players.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-1">Assessed</p>
          <p className="text-2xl font-bold text-green-600">{assessedCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-500 mb-1">Pending</p>
          <p className="text-2xl font-bold text-gray-400">{pendingCount}</p>
        </div>
      </div>

      {/* Empty State */}
      {players.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No Players Yet</h2>
          <p className="text-sm text-gray-500 max-w-sm mx-auto mb-6">
            Get started by adding your first player or importing your entire squad from a CSV file.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setShowAddPlayer(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]"
            >
              <UserPlus className="w-4 h-4" />
              Add Player
            </button>
            <button
              onClick={() => setShowImportCSV(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
            >
              <Upload className="w-4 h-4" />
              Import CSV
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* View Toggle + Add Button */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('position')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors min-h-[36px] ${
                  viewMode === 'position' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                By Position
              </button>
              <button
                onClick={() => setViewMode('alphabetical')}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors min-h-[36px] ${
                  viewMode === 'alphabetical' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <SortAsc className="w-3.5 h-3.5" />
                Alphabetical
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowImportCSV(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Import</span>
              </button>
              <button
                onClick={() => setShowAddPlayer(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]"
              >
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Player</span>
              </button>
            </div>
          </div>

          {/* Player List */}
          {viewMode === 'position' ? (
            <div className="space-y-8">
              {groupedByPosition.map(group => (
                <div key={group.position}>
                  <h3 className="text-sm font-semibold text-gray-500 text-center mb-3">
                    {group.label} ({group.players.length})
                  </h3>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {group.players.map(p => <PlayerCard key={p.id} player={p} />)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {alphabeticalPlayers.map(p => <PlayerCard key={p.id} player={p} />)}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showAddPlayer && selectedTeam && (
        <CreatePlayerModal
          teamId={selectedTeam}
          onClose={() => setShowAddPlayer(false)}
          onPlayerCreated={handlePlayerCreated}
        />
      )}
      {showImportCSV && selectedTeam && (
        <ImportPlayersModal
          teamId={selectedTeam}
          onClose={() => setShowImportCSV(false)}
          onPlayersImported={handlePlayersImported}
        />
      )}
    </div>
  );
}
